import { useAtom } from 'jotai';
import { useEffect, useCallback } from 'react';
import { featureFlagsAtom, featureFlagsLoadingAtom, featureFlagsErrorAtom, featureFlagsCacheAtom, isCacheValidAtom, validCacheDataAtom, } from './atom';
import { FeatureFlagState } from '@ibk/shared-types';
import { useGetFeatureFlagsQuery, useLazyGetFeatureFlagsQuery, } from '../services/featureFlagsService';
import { config } from '@ibk/shared-config';
import { ignoredFeatureFlagsRoutes } from './types/utils';
export const useFeatureFlag = (userId, options) => {
    const { autoLoad = true, isLoggedIn = true } = options || {};
    const [featureFlags, setFeatureFlags] = useAtom(featureFlagsAtom);
    const [isLoading, setIsLoading] = useAtom(featureFlagsLoadingAtom);
    const [error, setError] = useAtom(featureFlagsErrorAtom);
    const [cache, setCache] = useAtom(featureFlagsCacheAtom);
    const [isCacheValid] = useAtom(isCacheValidAtom);
    const [validCacheData] = useAtom(validCacheDataAtom);
    const regularQueryResult = useGetFeatureFlagsQuery(undefined, {
        skip: !autoLoad || !isLoggedIn || ignoredFeatureFlagsRoutes.includes(location.pathname),
    });
    const [triggerGetFeatureFlags, lazyQueryResult] = useLazyGetFeatureFlagsQuery();
    const rtqData = autoLoad ? regularQueryResult.data : lazyQueryResult.data;
    const rtqLoading = autoLoad ? regularQueryResult.isLoading : lazyQueryResult.isLoading;
    const rtqError = autoLoad ? regularQueryResult.error : lazyQueryResult.error;
    const refetch = autoLoad ? regularQueryResult.refetch : undefined;
    const cacheTtl = parseInt(config.featureFlagsCacheTtl, 10);
    const getFeatureFlags = useCallback(async (forceRefresh = false) => {
        if (!forceRefresh && isCacheValid && validCacheData) {
            return validCacheData;
        }
        setIsLoading(true);
        setError(null);
        try {
            let result;
            if (autoLoad && refetch) {
                result = await refetch();
            }
            else {
                result = await triggerGetFeatureFlags();
            }
            if (result.data) {
                const now = Date.now();
                setCache({
                    data: result.data,
                    timestamp: now,
                    ttl: cacheTtl,
                });
                setFeatureFlags(result.data);
                return result.data;
            }
            else {
                throw new Error('Dados não encontrados');
            }
        }
        catch (err) {
            const errorMessage = err.message || 'Erro ao buscar feature flags';
            setError(errorMessage);
            const fallbackData = {
                featureFlags: [],
                version: '1.0.0',
                timestamp: new Date().toISOString(),
            };
            return fallbackData;
        }
        finally {
            setIsLoading(false);
        }
    }, [
        isCacheValid,
        validCacheData,
        cacheTtl,
        autoLoad,
        refetch,
        triggerGetFeatureFlags,
        setIsLoading,
        setError,
        setCache,
        setFeatureFlags,
    ]);
    const isFeatureFlagWhiteListed = useCallback((useCase, userIdParam, featureFlagsData) => {
        const currentUserId = userIdParam ?? userId;
        const currentData = featureFlagsData || featureFlags || validCacheData || rtqData;
        if (!currentData?.featureFlags) {
            return false;
        }
        const flag = currentData.featureFlags.find((f) => f.useCase === useCase);
        if (!flag?.enabled) {
            return false;
        }
        if (flag.whitelistedUsers && flag.whitelistedUsers.length > 0) {
            return currentUserId ? flag.whitelistedUsers.includes(currentUserId) : false;
        }
        return flag.state === FeatureFlagState.WHITELISTED;
    }, [featureFlags, validCacheData, rtqData, userId]);
    const isFeatureFlagBlackListed = useCallback((useCase, userIdParam) => {
        const currentUserId = userIdParam ?? userId;
        const currentData = featureFlags || validCacheData || rtqData;
        if (!currentData?.featureFlags) {
            return false;
        }
        const flag = currentData.featureFlags.find((f) => f.useCase === useCase);
        if (!flag) {
            return false;
        }
        if (flag.blacklistedUsers && flag.blacklistedUsers.length > 0) {
            return currentUserId ? flag.blacklistedUsers.includes(currentUserId) : false;
        }
        return flag.state === FeatureFlagState.BLACKLISTED;
    }, [featureFlags, validCacheData, rtqData, userId]);
    const getFlagPerAppName = useCallback((domain, useCase) => {
        const currentData = featureFlags || validCacheData || rtqData;
        if (!currentData?.featureFlags) {
            return null;
        }
        return (currentData.featureFlags.find((f) => f.useCase === useCase && f.domain === domain) || null);
    }, [featureFlags, validCacheData, rtqData]);
    const clearCache = useCallback(() => {
        setCache(null);
        setFeatureFlags(null);
        setError(null);
    }, [setCache, setFeatureFlags, setError]);
    const refetchFeatureFlags = useCallback(async () => {
        clearCache();
        console.log('refetchFeatureFlags...');
        await getFeatureFlags(true);
    }, [getFeatureFlags, clearCache]);
    useEffect(() => {
        if (autoLoad &&
            isLoggedIn &&
            !featureFlags &&
            !rtqData &&
            !isLoading &&
            !rtqLoading &&
            !isCacheValid) {
            getFeatureFlags();
        }
    }, [autoLoad, isLoggedIn, featureFlags, rtqData, isLoading, rtqLoading, isCacheValid]);
    useEffect(() => {
        if (rtqData && !featureFlags) {
            setFeatureFlags(rtqData);
        }
    }, [rtqData, featureFlags, setFeatureFlags]);
    useEffect(() => {
        if (rtqError && !error) {
            setError(rtqError.toString());
        }
    }, [rtqError, error, setError]);
    useEffect(() => {
        setIsLoading(rtqLoading);
    }, [rtqLoading, setIsLoading]);
    return {
        isFeatureFlagWhiteListed,
        isFeatureFlagBlackListed,
        getFlagPerAppName,
        getFeatureFlags,
        clearCache,
        isLoading: isLoading || rtqLoading,
        error: error || (rtqError ? rtqError.toString() : null),
        refetch: refetchFeatureFlags,
        cache,
    };
};
